import type { LapEntry } from '@/types/telemetry';
import { formatTime, formatDelta, rawSpeed } from '@/lib/format';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { CheckCircle2, Clock, GitCompareArrows } from 'lucide-react';

interface LapTableProps {
  sessionId: string;
  laps: LapEntry[];
  allTimeBestLapMs: number;
  allTimeBestSector: Record<string, number>;
  trackDisplayName: string;
  tyreStartAge?: number;
  bestSectorsMs?: number[];
}

const SECTOR_KEYS = ['sector1Ms', 'sector2Ms', 'sector3Ms', 'sector4Ms', 'sector5Ms', 'sector6Ms'];

export function LapTable({ sessionId, laps, allTimeBestLapMs, allTimeBestSector, trackDisplayName, tyreStartAge, bestSectorsMs }: LapTableProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  if (!laps || laps.length === 0) return null;

  // Determine max sector count
  let maxSectorCount = 0;
  for (const lapEntry of laps) {
    const lap = lapEntry.lap;
    for (let i = SECTOR_KEYS.length - 1; i >= maxSectorCount; i--) {
      if ((lap as any)[SECTOR_KEYS[i]] > 0) {
        maxSectorCount = i + 1;
        break;
      }
    }
  }

  // Session best per sector
  const sessionBestSector: Record<string, number> = {};
  if (bestSectorsMs && bestSectorsMs.length > 0) {
    SECTOR_KEYS.forEach((k, i) => {
      if (bestSectorsMs[i]) {
        sessionBestSector[k] = bestSectorsMs[i];
      }
    });
  }

  const showTyreAge = tyreStartAge != null;

  return (
    <div className="card p-0 overflow-hidden mb-6">
      <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5 pb-4 border-b border-border-subtle">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
          {t('lapTable.title')}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { const qp = new URLSearchParams(); qp.set('session', sessionId); navigate(`#/analysis/aggregation/${encodeURIComponent(trackDisplayName)}?${qp.toString()}`); }}
            className="inline-flex items-center gap-1 text-[0.7rem] font-medium text-green opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
          >
            📊 {t('lapTable.aggregation')}
          </button>
          <button
            onClick={() => { const qp = new URLSearchParams(); qp.set('session', sessionId); navigate(`#/analysis/aggregation-diff/${encodeURIComponent(trackDisplayName)}?${qp.toString()}`); }}
            className="inline-flex items-center gap-1 text-[0.7rem] font-medium text-accent opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
          >
            <GitCompareArrows size={12} />
            {t('lapTable.compare')}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px] border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted sticky left-0 bg-bg-surface z-10 border-r border-border-subtle">#</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.lapTime')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.delta')}</th>
              {Array.from({ length: maxSectorCount }, (_, i) => (
                <th key={i} className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">S{i + 1}</th>
              ))}
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.maxRpm')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.minRpm')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.maxSpeed')}</th>
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.minSpeed')}</th>
              {showTyreAge && (
                <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('lapTable.tyreAge')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {laps.map((l, index) => {
              const lap = l.lap;
              const isBest = l.isBest;
              const isProcessed = l.analysisStatus === 'completed';

              // Lap time colour
              const isAllTimeBest = allTimeBestLapMs > 0 && lap.lapTimeMs > 0 && lap.lapTimeMs === allTimeBestLapMs;
              const lapTimeCls = isAllTimeBest ? 'td-purple' : isBest ? 'td-green' : '';

              const deltaClass = l.deltaToBestMs > 0 ? 'text-red' : 'text-green';

              return (
                <tr
                  key={lap.lapNumber}
                  onClick={() => navigate(`#/session/${sessionId}/lap/${lap.lapNumber}`)}
                  className={`hover:bg-bg-hover cursor-pointer transition-colors ${index < laps.length - 1 ? 'divider' : ''} ${isBest ? 'best-lap' : ''}`}
                >
                  <td className="py-2.5 px-4 sticky left-0 bg-bg-surface z-10 border-r border-border-subtle text-text-secondary mono font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <span>{lap.lapNumber}</span>
                      {isProcessed ? (
                        <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400">
                          <CheckCircle2 size={13} />
                        </span>
                      ) : (
                        <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400">
                          <Clock size={13} />
                        </span>
                      )}
                      {isBest && <span className="text-green text-[0.7rem]">★</span>}
                    </span>
                  </td>
                  <td className={`py-2.5 px-4 mono font-semibold text-right ${lapTimeCls}`}>
                    {formatTime(lap.lapTimeMs)}
                  </td>
                  <td className={`py-2.5 px-4 mono text-right ${deltaClass}`}>
                    {formatDelta(l.deltaToBestMs)}
                  </td>
                  {SECTOR_KEYS.slice(0, maxSectorCount).map(k => {
                    const val = (lap as any)[k] as number;
                    if (!val || val <= 0) return <td key={k} className="py-2.5 px-4 mono text-right text-text-muted">—</td>;
                    const isAtBest = allTimeBestSector[k] > 0 && val === allTimeBestSector[k];
                    const isSessBest = sessionBestSector[k] > 0 && val === sessionBestSector[k];
                    const cls = isAtBest ? 'td-purple' : isSessBest ? 'td-green' : '';
                    return (
                      <td key={k} className={`py-2.5 px-4 mono text-right ${cls}`}>
                        {formatTime(val)}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-4 mono text-right text-text-secondary">{lap.maxRpm ?? '—'}</td>
                  <td className="py-2.5 px-4 mono text-right text-text-secondary">{lap.minRpm ?? '—'}</td>
                  <td className="py-2.5 px-4 mono text-right text-text-secondary whitespace-nowrap">
                    {lap.maxSpeedGpsRaw ? rawSpeed(lap.maxSpeedGpsRaw).toFixed(1) + ' km/h' : '—'}
                  </td>
                  <td className="py-2.5 px-4 mono text-right text-text-secondary whitespace-nowrap">
                    {lap.minSpeedGpsRaw ? rawSpeed(lap.minSpeedGpsRaw).toFixed(1) + ' km/h' : '—'}
                  </td>
                  {showTyreAge && (
                    <td className="py-2.5 px-4 mono text-right text-text-muted">
                      {tyreStartAge! + lap.lapNumber}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
